import { defineStore } from 'pinia';
import { httpAPI } from '@/services/ajax';
import {
    clearSupportSession,
    readSupportSession,
    writeSupportSession,
} from '@/services/supportSession';
import app from '@/main.js';

// Client state for app admin support mode. The server stays authoritative for access.
export const useSupportStore = defineStore('support', {
    state: () => ({
        session: readSupportSession(),
        company: null,
        companyPromise: null,
    }),
    getters: {
        active: (state) => Boolean(state.session),
        companyId: (state) => state.session?.companyId ?? null,
        companyName: (state) =>
            state.company?.name || state.session?.companyName || '',
    },
    actions: {
        start(session) {
            this.session = writeSupportSession(session);
            this.company = null;
            this.companyPromise = null;
        },

        // Loads the supported company. Company permission checks then use it instead of the admin's own company.
        loadCompany() {
            if (!this.session || !app) return Promise.resolve(null);
            if (this.company?.id === this.session.companyId) {
                return Promise.resolve(this.company);
            }
            if (this.companyPromise) return this.companyPromise;

            const url =
                app.config.globalProperties.$env.apiRoot + 'company/read';
            this.companyPromise = httpAPI(
                url,
                {
                    where: { id: this.session.companyId },
                    include: { sector: true },
                },
                'GET',
                app.config.globalProperties.$router
            )
                .then((response) => {
                    const company = response?.success
                        ? response.data?.[0]
                        : null;
                    if (Number(company?.id) !== this.session?.companyId) {
                        throw new Error('Support company was not found.');
                    }
                    this.company = company;
                    return company;
                })
                .finally(() => {
                    this.companyPromise = null;
                });
            return this.companyPromise;
        },

        exit() {
            clearSupportSession();
            this.session = null;
            this.company = null;
            this.companyPromise = null;
        },
    },
});
